
import React, { useRef } from "react";
import { LogoExport } from "@/components/ui/logo-export";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Download, Copy } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import html2canvas from "html2canvas";

const LogoExportPage = () => {
  const { toast } = useToast();

  const exportAsImage = async (elementId: string, filename: string) => {
    try {
      const element = document.getElementById(elementId);
      if (!element) {
        toast({
          title: "Error",
          description: "Logo element not found.",
          variant: "destructive",
        });
        return;
      }

      // Use html2canvas to capture the element
      const canvas = await html2canvas(element, {
        backgroundColor: null, // Transparent background
        scale: 2, // Higher resolution
        useCORS: true,
        allowTaint: true,
      });

      // Convert canvas to blob
      canvas.toBlob((blob) => {
        if (blob) {
          // Create download link
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.download = filename;
          link.href = url;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          toast({
            title: "Success!",
            description: `${filename} downloaded successfully.`,
          });
        }
      }, 'image/png');
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export Error",
        description: "Failed to export logo. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">SnapRefund Logo Export</h1>
        <p className="text-muted-foreground">
          Export your SnapRefund logo in various formats and sizes for social media and marketing use.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Social Media Formats */}
        <Card>
          <CardHeader>
            <CardTitle>Social Media Profile (Square)</CardTitle>
            <p className="text-sm text-muted-foreground">Perfect for profile pictures - 1:1 ratio</p>
          </CardHeader>
          <CardContent>
            <div id="logo-square" className="bg-white border rounded-lg">
              <LogoExport size="xl" format="square" backgroundColor="white" />
            </div>
            <div className="flex gap-2 mt-4">
              <Button 
                onClick={() => exportAsImage("logo-square", "snaprefund-logo-square.png")}
                size="sm"
              >
                <Download className="h-4 w-4 mr-2" />
                Export PNG
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Social Media Header</CardTitle>
            <p className="text-sm text-muted-foreground">For banners and headers - horizontal layout</p>
          </CardHeader>
          <CardContent>
            <div id="logo-horizontal" className="bg-white border rounded-lg">
              <LogoExport size="lg" format="horizontal" backgroundColor="white" />
            </div>
            <div className="flex gap-2 mt-4">
              <Button 
                onClick={() => exportAsImage("logo-horizontal", "snaprefund-logo-horizontal.png")}
                size="sm"
              >
                <Download className="h-4 w-4 mr-2" />
                Export PNG
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Dark Background Versions */}
        <Card>
          <CardHeader>
            <CardTitle>Dark Background (Square)</CardTitle>
            <p className="text-sm text-muted-foreground">For dark themes and backgrounds</p>
          </CardHeader>
          <CardContent>
            <div id="logo-dark-square" className="bg-gray-900 border rounded-lg">
              <LogoExport size="xl" format="square" backgroundColor="#111827" />
            </div>
            <div className="flex gap-2 mt-4">
              <Button 
                onClick={() => exportAsImage("logo-dark-square", "snaprefund-logo-dark-square.png")}
                size="sm"
              >
                <Download className="h-4 w-4 mr-2" />
                Export PNG
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Large Display</CardTitle>
            <p className="text-sm text-muted-foreground">High resolution for presentations and print</p>
          </CardHeader>
          <CardContent>
            <div id="logo-large" className="bg-white border rounded-lg">
              <LogoExport size="2xl" format="horizontal" backgroundColor="white" />
            </div>
            <div className="flex gap-2 mt-4">
              <Button 
                onClick={() => exportAsImage("logo-large", "snaprefund-logo-large.png")}
                size="sm"
              >
                <Download className="h-4 w-4 mr-2" />
                Export PNG
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Transparent Background Versions */}
        <Card>
          <CardHeader>
            <CardTitle>Transparent Background (Square)</CardTitle>
            <p className="text-sm text-muted-foreground">Logo with transparent background</p>
          </CardHeader>
          <CardContent>
            <div id="logo-transparent-square" className="border rounded-lg" style={{ 
              background: 'repeating-conic-gradient(#ccc 0% 25%, transparent 0% 50%) 50% / 20px 20px' 
            }}>
              <LogoExport size="xl" format="square" backgroundColor="transparent" />
            </div>
            <div className="flex gap-2 mt-4">
              <Button 
                onClick={() => exportAsImage("logo-transparent-square", "snaprefund-logo-transparent-square.png")}
                size="sm"
              >
                <Download className="h-4 w-4 mr-2" />
                Export PNG
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Transparent Background (Horizontal)</CardTitle>
            <p className="text-sm text-muted-foreground">Logo with transparent background</p>
          </CardHeader>
          <CardContent>
            <div id="logo-transparent-horizontal" className="border rounded-lg" style={{ 
              background: 'repeating-conic-gradient(#ccc 0% 25%, transparent 0% 50%) 50% / 20px 20px' 
            }}>
              <LogoExport size="lg" format="horizontal" backgroundColor="transparent" />
            </div>
            <div className="flex gap-2 mt-4">
              <Button 
                onClick={() => exportAsImage("logo-transparent-horizontal", "snaprefund-logo-transparent.png")}
                size="sm"
              >
                <Download className="h-4 w-4 mr-2" />
                Export PNG
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Usage Instructions */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Export Instructions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-medium mb-2">How to export:</h4>
            <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
              <li>Click "Export PNG" on any logo above</li>
              <li>The high-resolution PNG will automatically download</li>
              <li>Images are exported at 2x resolution for crisp quality</li>
              <li>Transparent versions have no background for versatile use</li>
            </ol>
          </div>
          
          <div>
            <h4 className="font-medium mb-2">Recommended sizes:</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
              <li><strong>Twitter/X Profile:</strong> 400x400px (square)</li>
              <li><strong>LinkedIn Profile:</strong> 300x300px (square)</li>
              <li><strong>Facebook Cover:</strong> 1200x630px (horizontal)</li>
              <li><strong>Instagram Profile:</strong> 320x320px (square)</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LogoExportPage;
